import type React from 'react';

/**
 * The frame every form field renders through: label line, the control, then helper or error
 * text. Spacing and sizing live here and in the `field-*` utilities (app/globals.css), never in
 * a field component, so a row of mixed fields (text, dropdown, toggle) always lines up.
 */

export interface FieldLabelProps {
  htmlFor?: string;
  /** Renders a plain, uncoloured " *" after the label text — never a coloured span. */
  required?: boolean;
  /** Right end of the label line, e.g. "Change slug". */
  labelAction?: React.ReactNode;
  children: React.ReactNode;
}

export function FieldLabel({ htmlFor, required, labelAction, children }: FieldLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 flex justify-between text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      <span>
        {children}
        {/* aria-hidden keeps the marker visible but out of the accessible name, so a field
            stays reachable by its plain label text — specs assert getByLabel(…, { exact: true }). */}
        {required ? <span aria-hidden="true"> *</span> : null}
      </span>
      {labelAction}
    </label>
  );
}

export interface FormFieldProps {
  /** The control's id: the label points at it, helper/error ids derive from it. */
  id: string;
  label?: React.ReactNode;
  /** No visible label, but keep its line so the control lines up beside labelled fields. */
  reserveLabelSpace?: boolean;
  required?: boolean;
  labelAction?: React.ReactNode;
  helperText?: React.ReactNode;
  /** Replaces helperText. */
  error?: string;
  /** A `count/limit` readout at the right end of the helper line, for a field with a length limit. */
  counter?: { count: number; limit: number };
  testId?: string;
  className?: string;
  children: React.ReactNode;
}

/** `aria-describedby` for a control rendered inside a FormField with these props, plus any
 *  outside description (e.g. a notice elsewhere in the form) the caller names. */
export function fieldDescribedBy(
  id: string,
  helperText?: React.ReactNode,
  error?: string,
  extra?: string,
  counter?: boolean,
): string | undefined {
  const own = error ? `${id}-error` : helperText ? `${id}-helper` : undefined;
  return [own, counter ? `${id}-counter` : undefined, extra].filter(Boolean).join(' ') || undefined;
}

export function FormField({
  id,
  label,
  reserveLabelSpace,
  required,
  labelAction,
  helperText,
  error,
  counter,
  testId,
  className,
  children,
}: FormFieldProps) {
  const message = error ? (
    <p id={`${id}-error`} role="alert" className="field-error">
      {error}
    </p>
  ) : helperText ? (
    <p id={`${id}-helper`} className="field-helper">
      {helperText}
    </p>
  ) : null;
  return (
    <div data-testid={testId} className={className}>
      {label ? (
        <FieldLabel htmlFor={id} required={required} labelAction={labelAction}>
          {label}
        </FieldLabel>
      ) : reserveLabelSpace ? (
        <div aria-hidden="true" className="invisible">
          <FieldLabel>&nbsp;</FieldLabel>
        </div>
      ) : null}
      {children}
      {counter ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">{message}</div>
          <p id={`${id}-counter`} className="field-helper shrink-0 tabular-nums">
            {counter.count}/{counter.limit}
          </p>
        </div>
      ) : (
        message
      )}
    </div>
  );
}
